import { Form, Formik } from 'formik';
import { produce } from 'immer';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { fetchCustomer, selectCustomerById } from '@sophocles/entities/Customer';
import { selectCustomerPreferencesById } from '@sophocles/entities/CustomerPreference/customerPreferenceSelectors';
import { useCreateSiteGroupMutation } from '@sophocles/features/entities/siteGroups';
import { addToast } from '@sophocles/features/toast';
import { siteGroupLandingPath } from '@sophocles/router/paths';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import SiteGroupForm from '@sophocles/views/siteGroups/forms/SiteGroupForm';

const SITE_GROUP_FIELDS = ['name', 'description', 'is_virtual', 'is_digital_noc', 'is_inventory'];

function CreateSiteGroupDialog({ open, customerId, onClose, onSubmit }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [createSiteGroup] = useCreateSiteGroupMutation();
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const customer = useSelector(selectCustomerById(customerId));
  const customerPreferences = useSelector(selectCustomerPreferencesById(customer?.preferences));
  const { isStaff, permissions } = useUser();
  const isBillboard = customer?.is_billboard ?? false;
  const alreadyHasDigitalNoc = customerPreferences?.digital_virtual_site_group != null;

  const handleClose = () => {
    onClose();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: '',
        description: '',
        is_virtual: false,
        is_digital_noc: null,
        is_inventory: false,
      }}
      validationSchema={yup.object().shape({
        name: yup.string('Name').required('Name is required'),
        description: yup.string('Description'),
        is_virtual: yup.boolean(),
        is_digital_noc: yup.boolean().nullable(),
        is_inventory: yup.boolean(),
      })}
      onSubmit={() => {}}
    >
      {({ isValid, values, setFieldValue, setFieldError, resetForm }) => (
        <ResponsiveDialog
          open={open}
          title={`${t('actions.create')} ${t('siteGroups.siteGroup')}`}
          canSubmit={isValid && customerId != 0}
          onSubmit={async () => {
            if (onSubmit) {
              return onSubmit(values);
            }
            setNonFieldMessages([]);
            const body = produce(values, draft => {
              draft.application = 1;
              draft.customer_id = customerId;
              return draft;
            });
            const res = await createSiteGroup({ body });
            if (res.error) {
              const messages = applyApiErrorToFormik(res.error, {
                setFieldError,
                knownFields: SITE_GROUP_FIELDS,
              });
              setNonFieldMessages(messages);
              if (messages.length > 0) {
                dispatch(
                  addToast({
                    message: t('siteGroups.createError', 'Failed to create {{term}}', {
                      term: t('siteGroups.siteGroup'),
                    }),
                    level: 'error',
                  }),
                );
              }
            } else {
              dispatch(fetchCustomer(customerId));
              navigate(siteGroupLandingPath(res.data, customer.id));
            }
          }}
          onClose={() => {
            resetForm();
            setNonFieldMessages([]);
            handleClose();
          }}
        >
          <Form>
            <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
            <SiteGroupForm
              values={values}
              setFieldValue={setFieldValue}
              isStaff={isStaff}
              isBillboard={isBillboard}
              alreadyHasDigitalNoc={alreadyHasDigitalNoc}
              canViewInventoryGroups={permissions.canViewInventoryGroups}
            />
          </Form>
        </ResponsiveDialog>
      )}
    </Formik>
  );
}

export default CreateSiteGroupDialog;
